import React, { useState, useMemo } from 'react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const REPORT_TYPES = {
  SALES_SUMMARY: {
    id: 'SALES_SUMMARY',
    label: 'Résumé des ventes',
    description: 'Résumé quotidien des ventes avec totaux et moyennes',
    fields: ['date', 'orders', 'revenue', 'averageOrderValue', 'confirmationRate']
  },
  REGIONAL_PERFORMANCE: {
    id: 'REGIONAL_PERFORMANCE',
    label: 'Performance régionale',
    description: 'Analyse des ventes par région',
    fields: ['region', 'orders', 'revenue', 'confirmationRate']
  },
  CUSTOMER_ANALYSIS: {
    id: 'CUSTOMER_ANALYSIS',
    label: 'Analyse client',
    description: 'Détails des clients et leurs commandes',
    fields: ['customerPhone', 'orderCount', 'totalSpent', 'lastOrderDate']
  }
};

const DATE_RANGES = [
  { id: '7d', label: '7 derniers jours' },
  { id: '30d', label: '30 derniers jours' },
  { id: '90d', label: '90 derniers jours' },
  { id: 'all', label: 'Toutes les données' }
];

const CustomReports = ({ orders }) => {
  const [selectedReport, setSelectedReport] = useState(REPORT_TYPES.SALES_SUMMARY.id);
  const [dateRange, setDateRange] = useState('30d');
  const [generating, setGenerating] = useState(false);

  // Filtrer les commandes par date
  const filteredOrders = useMemo(() => {
    if (dateRange === 'all') return orders;

    const now = new Date();
    const daysAgo = {
      '7d': 7,
      '30d': 30,
      '90d': 90
    }[dateRange];

    const cutoffDate = new Date(now);
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

    return orders.filter(order => {
      const orderDate = order.createdAt?.seconds 
        ? new Date(order.createdAt.seconds * 1000) 
        : new Date(order.createdAt);
      return orderDate >= cutoffDate;
    });
  }, [orders, dateRange]);

  // Générer les données du rapport
  const generateReportData = () => {
    switch (selectedReport) {
      case 'SALES_SUMMARY':
        return generateSalesSummary();
      case 'REGIONAL_PERFORMANCE':
        return generateRegionalPerformance();
      case 'CUSTOMER_ANALYSIS':
        return generateCustomerAnalysis();
      default:
        return [];
    }
  };

  const generateSalesSummary = () => {
    const dailyData = {};
    
    filteredOrders.forEach(order => {
      const date = order.createdAt?.seconds 
        ? new Date(order.createdAt.seconds * 1000) 
        : new Date(order.createdAt);
      const dateStr = date.toISOString().split('T')[0];
      
      if (!dailyData[dateStr]) {
        dailyData[dateStr] = {
          date: dateStr,
          orders: 0,
          revenue: 0,
          confirmed: 0
        };
      }
      
      dailyData[dateStr].orders++;
      dailyData[dateStr].revenue += order.totalPrice || 0;
      if (order.status === 'confirmed') {
        dailyData[dateStr].confirmed++;
      }
    });

    return Object.values(dailyData).map(day => ({
      date: new Date(day.date).toLocaleDateString('fr-DZ'),
      orders: day.orders,
      revenue: day.revenue.toLocaleString('fr-DZ') + ' DA',
      averageOrderValue: Math.round(day.revenue / day.orders).toLocaleString('fr-DZ') + ' DA',
      confirmationRate: day.orders ? Math.round((day.confirmed / day.orders) * 100) + '%' : '0%'
    }));
  };

  const generateRegionalPerformance = () => {
    const regions = {
      'Centre': ['Alger', 'Blida', 'Bouira', 'Boumerdes', 'Médéa', 'Tizi Ouzou', 'Tipaza'],
      'Est': ['Annaba', 'Constantine', 'Sétif', 'Batna', 'Béjaïa', 'Jijel', 'Skikda'],
      'Ouest': ['Oran', 'Tlemcen', 'Mostaganem', 'Sidi Bel Abbès', 'Mascara', 'Aïn Témouchent'],
      'Sud': ['Ghardaïa', 'Ouargla', 'Biskra', 'El Oued', 'Béchar', 'Adrar', 'Tamanrasset']
    };

    const regionalData = {};
    
    filteredOrders.forEach(order => {
      const wilaya = order.shippingAddress?.wilaya;
      if (!wilaya) return;

      const region = Object.entries(regions).find(([_, wilayas]) => 
        wilayas.includes(wilaya)
      )?.[0] || 'Autre';

      if (!regionalData[region]) {
        regionalData[region] = {
          region,
          orders: 0,
          revenue: 0,
          confirmed: 0
        };
      }

      regionalData[region].orders++;
      regionalData[region].revenue += order.totalPrice || 0;
      if (order.status === 'confirmed') {
        regionalData[region].confirmed++;
      }
    });

    return Object.values(regionalData).map(region => ({
      region: region.region,
      orders: region.orders,
      revenue: region.revenue.toLocaleString('fr-DZ') + ' DA',
      confirmationRate: region.orders ? Math.round((region.confirmed / region.orders) * 100) + '%' : '0%'
    }));
  };

  const generateCustomerAnalysis = () => {
    const customerData = {};
    
    filteredOrders.forEach(order => {
      const phone = order.customerPhone;
      if (!phone) return;

      if (!customerData[phone]) {
        customerData[phone] = {
          customerPhone: phone,
          orderCount: 0,
          totalSpent: 0,
          lastOrderDate: null
        };
      }

      customerData[phone].orderCount++;
      customerData[phone].totalSpent += order.totalPrice || 0;
      
      const orderDate = order.createdAt?.seconds 
        ? new Date(order.createdAt.seconds * 1000) 
        : new Date(order.createdAt);
      
      if (!customerData[phone].lastOrderDate || orderDate > customerData[phone].lastOrderDate) {
        customerData[phone].lastOrderDate = orderDate;
      }
    });

    return Object.values(customerData).map(customer => ({
      customerPhone: customer.customerPhone,
      orderCount: customer.orderCount,
      totalSpent: customer.totalSpent.toLocaleString('fr-DZ') + ' DA',
      lastOrderDate: customer.lastOrderDate.toLocaleDateString('fr-DZ')
    }));
  };

  // Exporter en Excel
  const exportToExcel = async () => {
    setGenerating(true);
    try {
      const data = generateReportData();
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Rapport");
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const fileName = `rapport_${selectedReport.toLowerCase()}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(dataBlob, fileName);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Rapports personnalisés</h3>
        
        <div className="space-y-4">
          {/* Sélection du type de rapport */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de rapport
            </label>
            <select
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {Object.values(REPORT_TYPES).map(type => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {REPORT_TYPES[selectedReport].description}
            </p>
          </div>

          {/* Sélection de la période */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Période
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {DATE_RANGES.map(range => (
                <option key={range.id} value={range.id}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          {/* Aperçu des données */}
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Aperçu</h4>
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {REPORT_TYPES[selectedReport].fields.map(field => (
                        <th
                          key={field}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {field}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {generateReportData().slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        {REPORT_TYPES[selectedReport].fields.map(field => (
                          <td
                            key={field}
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            {row[field]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Affichage des 5 premières lignes sur {generateReportData().length}
            </p>
          </div>

          {/* Bouton d'export */}
          <div className="mt-6">
            <button
              onClick={exportToExcel}
              disabled={generating}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                generating ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {generating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Génération en cours...
                </>
              ) : (
                'Exporter en Excel'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReports;
